import React, { useState, useRef, useEffect } from "react";
import Modal from "../../components/common/Modal";
import './id-card-config.scss'
import Input from "../../components/common/Input";

import { studentsAPI, staffAPI } from '../../services/api';
import toast from 'react-hot-toast';

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/* ── Fixed paper dimensions (mm) — card grid is DERIVED from these
     plus the chosen card size, not hardcoded ── */
const PAPER_SIZES = {
    A4:      { pageW: 210, pageH: 297 },
    P13x19:  { pageW: 330, pageH: 483 },
    p12x36:  { pageW: 305, pageH: 914 },
};

/* ── Fixed card sizes (mm) — width x height, portrait orientation ── */
const CARD_SIZES = {
    cr80:  { label: "86mm x 54mm (CR80 standard)",         w: 54, h: 86 },
    badge: { label: "95mm x 60mm (slightly larger badge)", w: 60, h: 95 },
};

const GAP_MM = 1.3229;   // 5px converted to mm at 96dpi (5 / 96 * 25.4)
const MARGIN_MM = 6;

/* ── Compute how many cards fit per row/column for a given paper +
     fixed card size, with a fixed 5px gap between cards. Cards never
     resize — only the count per page changes. ── */
const computeGrid = (paperKey, cardKey) => {
    const paper = PAPER_SIZES[paperKey];
    const card  = CARD_SIZES[cardKey];

    const usableW = paper.pageW - MARGIN_MM * 2;
    const usableH = paper.pageH - MARGIN_MM * 2;

    const cols = Math.max(1, Math.floor((usableW + GAP_MM) / (card.w + GAP_MM)));
    const rows = Math.max(1, Math.floor((usableH + GAP_MM) / (card.h + GAP_MM)));

    return { cols, rows, cardW: card.w, cardH: card.h, pageW: paper.pageW, pageH: paper.pageH };
};

/* ── Timeout wrapper so a single hung capture can't block forever ── */
const withTimeout = (promise, ms, label) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
    ]);

const GenerateIdCards = () => {
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [filters, setFilters] = useState({
        type: "",
        class: "",
        section: "",
        designation: "",
    });

    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const [paperSize, setPaperSize] = useState("A4");
    const [cardSize, setCardSize] = useState("cr80");
    const [generating, setGenerating] = useState(false);
    const [generatingStep, setGeneratingStep] = useState("");

    const [backgroundImage, setBackgroundImage] = useState(null);
    const [textColor, setTextColor] = useState("#000000");

    const [orangeBackground, setOrangeBackground] = useState(null);
    const [orangeTextColor, setOrangeTextColor] = useState("#333333");
    const [orangeLogo, setOrangeLogo] = useState("https://www.logodesign.net/logo-new/flame-on-top-of-open-book-for-library-or-association-8996ld.png?nwm=1&nws=1&industry=school&txt_keyword=All");

    const fileInputRef = useRef(null);
    const colorInputRef = useRef(null);
    const orangeBgRef = useRef(null);
    const orangeColorRef = useRef(null);

    const [selectedCardDesign, setSelectedCardDesign] = useState("card1");

    const [horizontalBackground, setHorizontalBackground] = useState(null);
    const [horizontalTextColor, setHorizontalTextColor] = useState("#1f2937");
    const [horizontalLogo, setHorizontalLogo] = useState(null);
    const [studentPhoto, setStudentPhoto] = useState(null);
    const horizontalBgRef = useRef(null);
    const horizontalColorRef = useRef(null);

    /* ── Fetch students OR staff depending on filter.type ── */
    const fetchStudents = async () => {
        setLoading(true);
        try {
            if (filters.type === "Staff") {
                const params = { page, page_size: 15, search };
                if (filters.designation) params.designation = filters.designation;
                Object.keys(params).forEach(k => !params[k] && delete params[k]);

                const res = await staffAPI.list(params);
                const list = (res.data.results || res.data || []).map(s => ({
                    id: s.id,
                    full_name: s.full_name || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
                    first_name: s.first_name,
                    last_name: s.last_name,
                    fatherName: s.father_name || "-",
                    admission_number: s.employee_id || s.staff_id || "-",
                    class_name: s.designation || "-",
                    section: "-",
                    phone: s.phone,
                    address: s.address,
                    photo: s.photo,
                    print: false,
                    isStaff: true,
                }));
                setStudents(list);
                setCount(res.data.count || list.length);
            } else if (filters.type === "Student") {
                const params = {
                    page, page_size: 15, search,
                    class: filters.class, section: filters.section,
                };
                Object.keys(params).forEach(k => !params[k] && delete params[k]);

                const res = await studentsAPI.list(params);
                const list = (res.data.results || res.data || []).map(s => ({ ...s, print: s.print || false, isStaff: false }));
                setStudents(list);
                setCount(res.data.count || 0);
            } else {
                setStudents([]);
                setCount(0);
            }
        } catch (err) {
            toast.error(`Failed to load ${filters.type === "Staff" ? "staff" : "students"}`);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (filters.type) fetchStudents();
    }, [page, search, filters.type, filters.class, filters.section, filters.designation]);

    useEffect(() => {
        studentsAPI.classes()
            .then(r => setClasses(r.data.results || r.data))
            .catch(() => {});
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const next = { ...prev, [name]: value };
            if (name === "type") {
                next.class = "";
                next.section = "";
                next.designation = "";
                setStudents([]);
                setSelectAll(false);
            }
            return next;
        });
        setPage(1);
    };

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        setStudents(prev => prev.map(student => ({ ...student, print: checked })));
    };

    const togglePrint = (id) => {
        const updated = students.map(item => item.id === id ? { ...item, print: !item.print } : item);
        setStudents(updated);
        setSelectAll(updated.length > 0 && updated.every(s => s.print));
    };

    const deleteStudent = (id) => {
        if (window.confirm("Delete this record?")) {
            setStudents(students.filter(x => x.id !== id));
        }
    };

    const viewStudent = (student) => {
        setSelectedStudent(student);
        setIsEditMode(false);
        setShowViewModal(true);
    };

    const editStudent = (student) => {
        setSelectedStudent({ ...student });
        setIsEditMode(true);
        setShowViewModal(true);
    };

    const updateStudent = () => {
        setStudents(prev => prev.map(item => item.id === selectedStudent.id ? selectedStudent : item));
        setShowViewModal(false);
        alert("Updated Successfully");
    };

    const selectedPeople = students.filter(s => s.print);
    const grid = computeGrid(paperSize, cardSize);
    const perPage = grid.cols * grid.rows;

    /* ── Wait for all images in the print container to load ── */
    const waitForImages = async (container) => {
        const images = Array.from(container.querySelectorAll("img"));
        await Promise.all(
            images.map(img => {
                if (img.complete && img.naturalWidth > 0) return Promise.resolve();
                return new Promise(resolve => {
                    const done = () => resolve();
                    img.addEventListener("load", done, { once: true });
                    img.addEventListener("error", () => {
                        img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
                        resolve();
                    }, { once: true });
                    setTimeout(done, 4000);
                });
            })
        );
    };

    /* ── Capture every selected card into an array of {id, dataUrl} ── */
    const captureSelectedCards = async () => {
        const container = document.getElementById("pdfCardsContainer");
        if (!container) throw new Error("Print area not found in the DOM.");

        setGeneratingStep("Loading images…");
        await waitForImages(container);

        const results = [];
        for (let i = 0; i < selectedPeople.length; i++) {
            const person = selectedPeople[i];
            setGeneratingStep(`Rendering card ${i + 1} of ${selectedPeople.length}…`);

            const cardEl = document.getElementById(`print-card-${person.id}`);
            if (!cardEl) continue;

            const canvas = await withTimeout(
                html2canvas(cardEl, {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                    foreignObjectRendering: false,
                }),
                15000,
                `Card render for ${person.full_name}`
            );

            if (!canvas || canvas.width === 0 || canvas.height === 0) continue;
            const dataUrl = canvas.toDataURL("image/png");
            if (!dataUrl || dataUrl === "data:,") continue;

            results.push({ id: person.id, dataUrl, width: canvas.width, height: canvas.height });
        }
        return results;
    };

    /* ── Build the actual PDF document from captured card images.
         Card size is FIXED — never stretched — only the count of
         cards per page (grid.cols x grid.rows) changes per paper. ── */
    const buildPdf = (captured) => {
        let pdf;
        if (paperSize === "A4") {
            pdf = new jsPDF("p", "mm", "a4");
        } else {
            pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [grid.pageW, grid.pageH] });
        }

        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();

        // Center the whole grid block on the page so leftover space is
        // distributed evenly instead of bunched on one side
        const gridW = grid.cols * grid.cardW + (grid.cols - 1) * GAP_MM;
        const gridH = grid.rows * grid.cardH + (grid.rows - 1) * GAP_MM;
        const offsetX = Math.max(MARGIN_MM, (pageW - gridW) / 2);
        const offsetY = Math.max(MARGIN_MM, (pageH - gridH) / 2);

        for (let i = 0; i < captured.length; i++) {
            const slot = i % perPage;
            if (i > 0 && slot === 0) pdf.addPage();

            const col = slot % grid.cols;
            const row = Math.floor(slot / grid.cols);

            const x = offsetX + col * (grid.cardW + GAP_MM);
            const y = offsetY + row * (grid.cardH + GAP_MM);

            pdf.addImage(captured[i].dataUrl, "PNG", x, y, grid.cardW, grid.cardH);
        }

        return pdf;
    };

    /* ── Download as PDF ── */
    const downloadIdCardsPDF = async () => {
        if (selectedPeople.length === 0) {
            alert("Please select at least one record using the checkboxes before generating.");
            return;
        }

        setGenerating(true);
        setGeneratingStep("Starting…");

        try {
            const captured = await captureSelectedCards();

            if (captured.length === 0) {
                toast.error("Could not render any cards. Please check photos and try again.");
                return;
            }

            setGeneratingStep("Building PDF…");
            const pdf = buildPdf(captured);
            pdf.save(`ID-Cards-${selectedCardDesign}-${paperSize}.pdf`);
            toast.success(`Downloaded ${captured.length} ID card(s)`);

        } catch (err) {
            console.error("PDF generation failed:", err);
            toast.error(err.message || "Failed to generate PDF. Please check photos and try again.");
        } finally {
            setGenerating(false);
            setGeneratingStep("");
        }
    };

    /* ── Print directly (opens browser print dialog with the same
         fixed-size, page-fitted layout, no PDF library round-trip) ── */
    const printIdCards = async () => {
        if (selectedPeople.length === 0) {
            alert("Please select at least one record using the checkboxes before printing.");
            return;
        }

        setGenerating(true);
        setGeneratingStep("Preparing print preview…");

        try {
            const captured = await captureSelectedCards();
            if (captured.length === 0) {
                toast.error("Could not render any cards. Please check photos and try again.");
                return;
            }

            const win = window.open("", "_blank");
            if (!win) {
                toast.error("Popup blocked. Please allow popups for this site and try again.");
                return;
            }

            const pages = [];
            for (let i = 0; i < captured.length; i += perPage) {
                pages.push(captured.slice(i, i + perPage));
            }

            const pagesHTML = pages.map(group => `
                <div class="print-page">
                    ${group.map(c => `<img src="${c.dataUrl}" class="print-card-img" />`).join("")}
                </div>
            `).join("");

            win.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>ID Cards</title>
                    <style>
                        @page { size: ${grid.pageW}mm ${grid.pageH}mm; margin: ${MARGIN_MM}mm; }
                        body { margin: 0; }
                        .print-page {
                            display: flex;
                            flex-wrap: wrap;
                            align-content: flex-start;
                            gap: ${GAP_MM}mm;
                            width: ${grid.pageW - MARGIN_MM * 2}mm;
                            page-break-after: always;
                        }
                        .print-card-img {
                            width: ${grid.cardW}mm;
                            height: ${grid.cardH}mm;
                            object-fit: contain;
                            flex-shrink: 0;
                        }
                    </style>
                </head>
                <body>${pagesHTML}</body>
                </html>
            `);
            win.document.close();

            setTimeout(() => {
                win.focus();
                win.print();
            }, 500);

        } catch (err) {
            console.error("Print failed:", err);
            toast.error(err.message || "Failed to prepare print preview.");
        } finally {
            setGenerating(false);
            setGeneratingStep("");
        }
    };

    return (
        <div>
            <div className="card shadow">
                <div className="card-header">Generate ID Cards</div>
                <div className="card-body">
                    <div className="row mb-3">
                        {/* ── Design 1: vertical card ── */}
                        <div className="col-md-3 col-sm-6">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => fileInputRef.current.click()}>
                                    Upload Background
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setBackgroundImage(URL.createObjectURL(e.target.files[0]));
                                        }
                                    }}
                                />
                                <button type="button" className="btn btn-success btn-sm" onClick={() => colorInputRef.current.click()}>
                                    Text Color
                                </button>
                                <input
                                    ref={colorInputRef}
                                    type="color"
                                    value={textColor}
                                    style={{ display: "none" }}
                                    onChange={(e) => setTextColor(e.target.value)}
                                />
                            </div>

                            <div
                                className={`id-card ${backgroundImage ? "custom-bg" : ""}`}
                                style={{
                                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : "",
                                    color: textColor,
                                    backgroundSize: "cover",
                                    backgroundRepeat: "no-repeat",
                                }}
                            >
                                <div className="card-header-section" style={backgroundImage ? { background: "transparent" } : {}}>
                                    <div className="logo-row">
                                        <img
                                            src="https://static.vecteezy.com/system/resources/thumbnails/045/548/055/small/school-logo-design-template-vector.jpg"
                                            alt="logo"
                                            className="school-logo"
                                        />
                                        <div className="school-details">
                                            <h2>Bala Krishna</h2>
                                            <h6>Public School</h6>
                                        </div>
                                    </div>
                                    <div className="school-addr">
                                        <p className="m-0">Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072</p>
                                        <p className="m-0">Ph: 9339400600</p>
                                    </div>
                                </div>
                                <div className="student-photo-section">
                                    <img
                                        src="https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"
                                        alt="Student"
                                        className="student-photo"
                                    />
                                </div>
                                <div className="student-name">Ranjani</div>
                                <div className="student-details">
                                    <table className="table table-borderless table-sm mb-0">
                                        <tbody>
                                            <tr><td>F'Name:</td><td>Mr.Deepak Singhal</td></tr>
                                            <tr><td>D.O.B.:</td><td>23-09-2025</td></tr>
                                            <tr><td>Contact No.:</td><td>9339400600</td></tr>
                                            <tr><td>Address:</td><td>Tulasi Nagar</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="card-footer-section">
                                    <div className="class-section"><strong>Class : 1st</strong></div>
                                    <div className="signature-section">
                                        <img
                                            src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                                            alt="signature"
                                            className="signature"
                                        />
                                        <span>Principal Sign.</span>
                                    </div>
                                </div>
                            </div>

                            <div className="card-design-selection">
                                <div className="form-check card-radio-option">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="cardDesign"
                                        id="cardDesign1"
                                        value="card1"
                                        checked={selectedCardDesign === "card1"}
                                        onChange={(e) => setSelectedCardDesign(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="cardDesign1">Select Card Design 1</label>
                                </div>
                            </div>
                        </div>

                        {/* ── Design 2: orange card ── */}
                        <div className="col-md-3 col-sm-6">
                            <div className="d-flex align-items-center mb-1 gap-1">
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => orangeBgRef.current.click()}>
                                    Upload Background
                                </button>
                                <input
                                    ref={orangeBgRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setOrangeBackground(URL.createObjectURL(e.target.files[0]));
                                        }
                                    }}
                                />
                                <button type="button" className="btn btn-success btn-sm" onClick={() => orangeColorRef.current.click()}>
                                    Text color
                                </button>
                                <input
                                    ref={orangeColorRef}
                                    type="color"
                                    style={{ display: "none" }}
                                    value={orangeTextColor}
                                    onChange={(e) => setOrangeTextColor(e.target.value)}
                                />
                            </div>

                            <div
                                className={`orange-id-card ${orangeBackground ? "custom-bg" : ""}`}
                                style={{
                                    backgroundImage: orangeBackground ? `url(${orangeBackground})` : "none",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    color: orangeTextColor,
                                }}
                            >
                                <div className="orange-card-top">
                                    <div className="orange-school-name">
                                        {orangeLogo ? (
                                            <img src={orangeLogo} alt="School Logo" className="orange-school-logo" />
                                        ) : (
                                            <span style={{ color: orangeTextColor }}>
                                                ABC SCHOOL NAME
                                                <span className="orange-school-slogan" style={{ color: orangeTextColor }}>
                                                    SLOGAN HERE
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="orange-student-photo-wrapper">
                                    <img
                                        src="https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"
                                        alt=""
                                        className="orange-student-photo"
                                    />
                                </div>
                                <div className="orange-card-details">
                                    <div className="orange-detail-row"><div className="orange-label">ID</div><div className="orange-value">: 1234</div></div>
                                    <div className="orange-detail-row"><div className="orange-label">Name</div><div className="orange-value">: Name Here</div></div>
                                    <div className="orange-detail-row"><div className="orange-label">F/G Name</div><div className="orange-value">: Name Here</div></div>
                                    <div className="orange-detail-row"><div className="orange-label">Class</div><div className="orange-value">: Class Here</div></div>
                                    <div className="orange-detail-row"><div className="orange-label">Phone No.:</div><div className="orange-value">: 1234567890</div></div>
                                </div>
                                <div className="orange-signature">
                                    <span>Principal Sign:</span>
                                    <img
                                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                                        alt="signature"
                                        className="signature"
                                    />
                                </div>
                                <div className="orange-card-footer">
                                    Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072, Ph: 9339400600
                                </div>
                            </div>

                            <div className="card-design-selection">
                                <div className="form-check card-radio-option">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="cardDesign"
                                        id="cardDesign2"
                                        value="card2"
                                        checked={selectedCardDesign === "card2"}
                                        onChange={(e) => setSelectedCardDesign(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="cardDesign2">Select Card Design 2</label>
                                </div>
                            </div>
                        </div>

                        {/* ── Design 3: horizontal card ── */}
                        <div className="col-md-6 col-sm-12">
                            <div className="gap-2 mb-1">
                                <input
                                    ref={horizontalBgRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setHorizontalBackground(URL.createObjectURL(e.target.files[0]));
                                        }
                                    }}
                                />
                                <input
                                    ref={horizontalColorRef}
                                    type="color"
                                    style={{ display: "none" }}
                                    value={horizontalTextColor}
                                    onChange={(e) => setHorizontalTextColor(e.target.value)}
                                />
                                <div className="gap-2">
                                    <button type="button" className="btn btn-primary btn-sm me-2" onClick={() => horizontalBgRef.current.click()}>
                                        Upload Background
                                    </button>
                                    <button type="button" className="btn btn-success btn-sm" onClick={() => horizontalColorRef.current.click()}>
                                        Text Color
                                    </button>
                                </div>
                            </div>

                            <div
                                className={`horizontal-id-card ${horizontalBackground ? "horizontal-custom-bg" : ""}`}
                                style={{
                                    backgroundImage: horizontalBackground ? `url(${horizontalBackground})` : "none",
                                    color: horizontalTextColor,
                                }}
                            >
                                <div className="horizontal-card-header">
                                    <div className="horizontal-logo-section">
                                        {horizontalLogo ? (
                                            <img src={horizontalLogo} alt="" className="horizontal-school-logo" />
                                        ) : (
                                            <div className="horizontal-school-logo-placeholder">LOGO</div>
                                        )}
                                    </div>
                                    <div className="horizontal-school-info">
                                        <div className="horizontal-school-name">SHRI PUBLIC SCHOOL</div>
                                        <div className="horizontal-school-tagname">Public School</div>
                                    </div>
                                </div>

                                <div className="horizontal-card-body">
                                    <div className="horizontal-photo-column">
                                        <img
                                            src={studentPhoto || "https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"}
                                            alt=""
                                            className="horizontal-student-photo"
                                        />
                                        <img
                                            src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                                            alt=""
                                            className="horizontal-signature"
                                        />
                                        <div className="horizontal-principal-text">Principal</div>
                                    </div>
                                    <div className="horizontal-details-column">
                                        <div className="horizontal-row"><span>Name</span><span>: Neha Verma</span></div>
                                        <div className="horizontal-row"><span>F/G Name</span><span>: Ajay Verma</span></div>
                                        <div className="horizontal-row"><span>D.O.B</span><span>: 19-08-2005</span></div>
                                        <div className="horizontal-row"><span>Class</span><span>: Xth A</span></div>
                                        <div className="horizontal-row"><span>Roll No.</span><span>: 20</span></div>
                                        <div className="horizontal-row"><span>Mobile No.</span><span>: 124587XXXX</span></div>
                                    </div>
                                </div>

                                <div className="horizontal-card-footer">
                                    Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072
                                </div>
                            </div>

                            <div className="card-design-selection">
                                <div className="form-check card-radio-option">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="cardDesign"
                                        id="cardDesign3"
                                        value="card3"
                                        checked={selectedCardDesign === "card3"}
                                        onChange={(e) => setSelectedCardDesign(e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="cardDesign3">Select Card Design 3</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Filters: Student / Staff ── */}
                    <div className="row border-top pt-3">
                        <div className="col-md-3 col-sm-4">
                            <label className="form-label">Student / Staff</label>
                            <select className="form-select" name="type" value={filters.type} onChange={handleChange}>
                                <option value="">Select User</option>
                                <option value="Student">Student</option>
                                <option value="Staff">Staff</option>
                            </select>
                        </div>

                        {filters.type === "Student" && (
                            <>
                                <div className="col-md-3 col-sm-4">
                                    <label className="form-label">Class</label>
                                    <select className="form-select" name="class" value={filters.class} onChange={handleChange}>
                                        <option value="">Select Class</option>
                                        {classes.length > 0
                                            ? classes.map(c => <option key={c.id || c} value={c.id || c}>{c.name || c}</option>)
                                            : (
                                                <>
                                                    <option value="1">1</option>
                                                    <option value="5">5</option>
                                                    <option value="10">10</option>
                                                </>
                                            )
                                        }
                                    </select>
                                </div>
                                <div className="col-md-3 col-sm-4">
                                    <label className="form-label">Section</label>
                                    <select className="form-select" name="section" value={filters.section} onChange={handleChange}>
                                        <option value="">Select Section</option>
                                        <option value="A">A</option>
                                        <option value="B">B</option>
                                    </select>
                                </div>
                            </>
                        )}

                        {filters.type === "Staff" && (
                            <div className="col-md-3 col-sm-4">
                                <label className="form-label">Designation</label>
                                <select className="form-select" name="designation" value={filters.designation} onChange={handleChange}>
                                    <option value="">Select Designation</option>
                                    <option value="Admin">Admin</option>
                                    <option value="Teacher">Teacher</option>
                                    <option value="Principal">Principal</option>
                                    <option value="Accountant">Accountant</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="mt-4">
                        <button className="btn btn-success" onClick={fetchStudents} disabled={!filters.type}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            {students.length > 0 && (
                <div className="card mt-4">
                    <div className="card-header">
                        <span>{filters.type === "Staff" ? "Staff" : "Student"} Details</span>
                        <div className="d-flex gap-3 align-items-center">

                            <label className="pt-2">Card Size:</label>
                            <select
                                onChange={(e) => setCardSize(e.target.value)}
                                value={cardSize}
                                className="form-control"
                                disabled={generating}
                                style={{ minWidth: 230 }}
                            >
                                {Object.entries(CARD_SIZES).map(([key, size]) => (
                                    <option key={key} value={key}>{size.label}</option>
                                ))}
                            </select>

                            <label className="pt-2">Paper Size:</label>
                            <select
                                onChange={(e) => setPaperSize(e.target.value)}
                                value={paperSize}
                                className="form-control"
                                disabled={generating}
                                style={{ minWidth: 210 }}
                            >
                                <option value="A4">A4 ({computeGrid("A4", cardSize).cols}×{computeGrid("A4", cardSize).rows} per page)</option>
                                <option value="P13x19">13x19 inches ({computeGrid("P13x19", cardSize).cols}×{computeGrid("P13x19", cardSize).rows} per page)</option>
                                <option value="p12x36">12x36 inches ({computeGrid("p12x36", cardSize).cols}×{computeGrid("p12x36", cardSize).rows} per page)</option>
                            </select>

                            <button className="btn btn-outline-primary" onClick={printIdCards} disabled={generating || selectedPeople.length === 0}>
                                🖨 Print
                            </button>
                            <button className="btn-add" onClick={downloadIdCardsPDF} disabled={generating || selectedPeople.length === 0}>
                                {generating ? (generatingStep || "Generating…") : `Download ${selectedPeople.length || ""} ID Card${selectedPeople.length === 1 ? "" : "s"}`}
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {selectedPeople.length === 0 && (
                            <div className="alert alert-warning py-2 small mb-3">
                                ⚠ No records selected. Tick the checkboxes below before downloading or printing.
                            </div>
                        )}
                        {generating && (
                            <div className="alert alert-info py-2 small mb-3 d-flex align-items-center gap-2">
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                {generatingStep || "Working…"}
                            </div>
                        )}
                        <table className="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th>Photo</th>
                                    <th>Admission/Emp No</th>
                                    <th>Name</th>
                                    <th>{filters.type === "Staff" ? "Designation" : "Father Name"}</th>
                                    <th>{filters.type === "Staff" ? "-" : "Class"}</th>
                                    <th>Section</th>
                                    <th>Mobile</th>
                                    <th>Address</th>
                                    <th>Print &nbsp;<input type="checkbox" onChange={handleSelectAll} checked={selectAll} className="mt-1" /></th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student) => (
                                    <tr key={student.id} style={{ background: student.print ? "#f0f9ff" : "" }}>
                                        <td>
                                            {student.photo ? (
                                                <img src={student.photo} alt="" className="img-fluid rounded" style={{ width: 35, height: 35, objectFit: "cover" }} />
                                            ) : (
                                                <div className="student-avatar-placeholder" style={{ width: 35, height: 35, fontSize: 14 }}>
                                                    {`${(student.full_name || student.first_name || "").charAt(0).toUpperCase()}${(student.last_name || "").charAt(0).toUpperCase()}`}
                                                </div>
                                            )}
                                        </td>
                                        <td>{student.admission_number}</td>
                                        <td>{student.full_name}</td>
                                        <td>{student.isStaff ? student.class_name : student.fatherName}</td>
                                        <td>{student.isStaff ? "-" : student.class_name}</td>
                                        <td>{student.section}</td>
                                        <td>{student.phone}</td>
                                        <td>{student.address}</td>
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={!!student.print}
                                                onChange={() => togglePrint(student.id)}
                                            />
                                        </td>
                                        <td>
                                            <button className="btn btn-info btn-sm me-2" onClick={() => viewStudent(student)}>
                                                <i className="bi bi-eye"></i>
                                            </button>
                                            <button className="btn btn-danger btn-sm" onClick={() => deleteStudent(student.id)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Modal show={showViewModal} onClose={() => setShowViewModal(false)} title={isEditMode ? "Edit" : "Details"} size="lg">
                {selectedStudent && (
                    <div className="row">
                        <div className="col-md-4 text-center">
                            {selectedStudent.photo ? (
                                <img src={selectedStudent.photo} alt="" className="img-fluid rounded" />
                            ) : (
                                <div className="student-avatar-placeholder">
                                    {`${(selectedStudent.full_name || selectedStudent.first_name || "").charAt(0).toUpperCase()}${(selectedStudent.last_name || "").charAt(0).toUpperCase()}`}
                                </div>
                            )}
                        </div>
                        <div className="col-md-8">
                            {!isEditMode && (
                                <>
                                    <h4>{selectedStudent.full_name || `${selectedStudent.first_name || ""} ${selectedStudent.last_name || ""}`}</h4>
                                    <hr />
                                    <p><strong>{selectedStudent.isStaff ? "Designation" : "Father Name"}:</strong> {selectedStudent.isStaff ? selectedStudent.class_name : selectedStudent.fatherName}</p>
                                    <p><strong>ID No:</strong> {selectedStudent.admission_number}</p>
                                    {!selectedStudent.isStaff && <p><strong>Class:</strong> {selectedStudent.class_name}</p>}
                                    {!selectedStudent.isStaff && <p><strong>Section:</strong> {selectedStudent.section}</p>}
                                    <p><strong>Mobile:</strong> {selectedStudent.phone}</p>
                                    <p><strong>Address:</strong> {selectedStudent.address}</p>
                                </>
                            )}
                        </div>
                        <hr />
                        <p className="text-center mb-0"><b className="text-danger">Note:</b> If you want to edit details, go to the {selectedStudent.isStaff ? "Staff" : "Student"} Module.</p>
                    </div>
                )}
            </Modal>

            {/* ── Print source container: stays in normal layout flow so
                   html2canvas can actually measure/paint it, but invisible
                   to the user via opacity + negative z-index + pointer-events.
                   (position:absolute;left:-9999px frequently causes
                   html2canvas to hang or render blank — this fixes that.) ── */}
            <div
                id="pdfCardsContainer"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    opacity: 0,
                    pointerEvents: "none",
                    zIndex: -1,
                }}
            >
                {selectedPeople.map(person => (
                    <div key={person.id} id={`print-card-${person.id}`}>
                        {selectedCardDesign === "card1" && (
                            <PrintCard1 person={person} background={backgroundImage} textColor={textColor} />
                        )}
                        {selectedCardDesign === "card2" && (
                            <PrintCard2 person={person} background={orangeBackground} textColor={orangeTextColor} logo={orangeLogo} />
                        )}
                        {selectedCardDesign === "card3" && (
                            <PrintCard3 person={person} background={horizontalBackground} textColor={horizontalTextColor} logo={horizontalLogo} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ── Print versions of each design — mirrors the live preview markup,
     but takes per-person data + the user's chosen bg/color ── */
function PrintCard1({ person, background, textColor }) {
    return (
        <div
            className={`id-card print-card ${background ? "custom-bg" : ""}`}
            style={{
                backgroundImage: background ? `url(${background})` : "",
                color: textColor,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="card-header-section" style={background ? { background: "transparent" } : {}}>
                <div className="logo-row">
                    <img
                        src="https://static.vecteezy.com/system/resources/thumbnails/045/548/055/small/school-logo-design-template-vector.jpg"
                        alt="logo"
                        className="school-logo"
                        crossOrigin="anonymous"
                    />
                    <div className="school-details">
                        <h2>Bala Krishna</h2>
                        <h6>Public School</h6>
                    </div>
                </div>
                <div className="school-addr">
                    <p className="m-0">Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072</p>
                    <p className="m-0">Ph: 9339400600</p>
                </div>
            </div>
            <div className="student-photo-section">
                <img
                    src={person.photo || "https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"}
                    alt=""
                    className="student-photo"
                    crossOrigin="anonymous"
                />
            </div>
            <div className="student-name">{person.full_name}</div>
            <div className="student-details">
                <table className="table table-borderless table-sm mb-0">
                    <tbody>
                        <tr><td>F'Name:</td><td>{person.isStaff ? "-" : person.fatherName}</td></tr>
                        <tr><td>ID No.:</td><td>{person.admission_number}</td></tr>
                        <tr><td>Contact No.:</td><td>{person.phone}</td></tr>
                        <tr><td>Address:</td><td>{person.address}</td></tr>
                    </tbody>
                </table>
            </div>
            <div className="card-footer-section">
                <div className="class-section"><strong>{person.isStaff ? person.class_name : `Class: ${person.class_name} - ${person.section}`}</strong></div>
                <div className="signature-section">
                    <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                        alt="signature"
                        className="signature"
                        crossOrigin="anonymous"
                    />
                    <span>Principal Sign.</span>
                </div>
            </div>
        </div>
    );
}

function PrintCard2({ person, background, textColor, logo }) {
    return (
        <div
            className={`orange-id-card print-card ${background ? "custom-bg" : ""}`}
            style={{
                backgroundImage: background ? `url(${background})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                color: textColor,
            }}
        >
            <div className="orange-card-top">
                <div className="orange-school-name">
                    {logo ? (
                        <img src={logo} alt="School Logo" className="orange-school-logo" crossOrigin="anonymous" />
                    ) : (
                        <span style={{ color: textColor }}>
                            ABC SCHOOL NAME
                            <span className="orange-school-slogan" style={{ color: textColor }}>SLOGAN HERE</span>
                        </span>
                    )}
                </div>
            </div>
            <div className="orange-student-photo-wrapper">
                <img
                    src={person.photo || "https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"}
                    alt=""
                    className="orange-student-photo"
                    crossOrigin="anonymous"
                />
            </div>
            <div className="orange-card-details">
                <div className="orange-detail-row"><div className="orange-label">ID</div><div className="orange-value">: {person.admission_number}</div></div>
                <div className="orange-detail-row"><div className="orange-label">Name</div><div className="orange-value">: {person.full_name}</div></div>
                <div className="orange-detail-row"><div className="orange-label">{person.isStaff ? "Designation" : "F/G Name"}</div><div className="orange-value">: {person.isStaff ? person.class_name : person.fatherName}</div></div>
                {!person.isStaff && <div className="orange-detail-row"><div className="orange-label">Class</div><div className="orange-value">: {person.class_name} - {person.section}</div></div>}
                <div className="orange-detail-row"><div className="orange-label">Phone No.:</div><div className="orange-value">: {person.phone}</div></div>
            </div>
            <div className="orange-signature">
                <span>Principal Sign:</span>
                <img
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                    alt="signature"
                    className="signature"
                    crossOrigin="anonymous"
                />
            </div>
            <div className="orange-card-footer">
                Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072, Ph: 9339400600
            </div>
        </div>
    );
}

function PrintCard3({ person, background, textColor, logo }) {
    return (
        <div
            className={`horizontal-id-card print-card ${background ? "horizontal-custom-bg" : ""}`}
            style={{
                backgroundImage: background ? `url(${background})` : "none",
                color: textColor,
            }}
        >
            <div className="horizontal-card-header">
                <div className="horizontal-logo-section">
                    {logo ? (
                        <img src={logo} alt="" className="horizontal-school-logo" crossOrigin="anonymous" />
                    ) : (
                        <div className="horizontal-school-logo-placeholder">LOGO</div>
                    )}
                </div>
                <div className="horizontal-school-info">
                    <div className="horizontal-school-name">SHRI PUBLIC SCHOOL</div>
                    <div className="horizontal-school-tagname">Public School</div>
                </div>
            </div>
            <div className="horizontal-card-body">
                <div className="horizontal-photo-column">
                    <img
                        src={person.photo || "https://www.picsman.ai/blog/wp-content/uploads/2025/01/white-passport-photo-979x1024.webp"}
                        alt=""
                        className="horizontal-student-photo"
                        crossOrigin="anonymous"
                    />
                    <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS3Ktz-8vya-kHe-FFSfGoqhFJ67tDgwTyLifMHX08yetz-z0J09ZbSjQ8&s=10"
                        alt=""
                        className="horizontal-signature"
                        crossOrigin="anonymous"
                    />
                    <div className="horizontal-principal-text">Principal</div>
                </div>
                <div className="horizontal-details-column">
                    <div className="horizontal-row"><span>Name</span><span>: {person.full_name}</span></div>
                    <div className="horizontal-row"><span>{person.isStaff ? "Designation" : "F/G Name"}</span><span>: {person.isStaff ? person.class_name : person.fatherName}</span></div>
                    <div className="horizontal-row"><span>ID No.</span><span>: {person.admission_number}</span></div>
                    {!person.isStaff && <div className="horizontal-row"><span>Class</span><span>: {person.class_name} {person.section}</span></div>}
                    <div className="horizontal-row"><span>Mobile No.</span><span>: {person.phone}</span></div>
                </div>
            </div>
            <div className="horizontal-card-footer">
                Bhudevi Hill, Allwyn Colony, Phase-22, Kukatpally, Hyderabad-500072
            </div>
        </div>
    );
}

export default GenerateIdCards;